// @flow
import { store } from "common/store";
import { View } from "extensions/marionette";
import { Backbone } from "extensions/backbone";
import Cleave from "cleave.js";
import sweetalert from "sweetalert";
import moment from "moment";

import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { Dropdown } from "common/components/dropdown/dropdown";
import { DatePicker } from "common/components/datePicker/datePicker";

import { AmountPicker } from "./amountPicker";
import { SearchFormModel } from "../searchModel";

import sales_refunds_template from "./searchForm_sales_refunds.jst";
import chargebacks_template from "./searchForm_chargebacks.jst";
import authorizations_template from "./searchForm_authorizations.jst";

type DropDownItemType = { name: string, value: string, selected?: boolean };
type inputFieldType = { selector: string };
type DropDownFieldType = { selector: string, apiKey: string, useTypeAheadCollection: boolean };
type AmountPickerFieldType = {
    primary: boolean,
    selector: string,
    input: inputFieldType,
    dropdown: DropDownFieldType,
    pickerRoot: "purchase_" | "stl_",
    label: string,
    labelprefix: "Purchase" | "Settlement" | "Chargeback"
};

export const SearchForm = View.extend({
    tagName: "form",
    className: "search-form",
    getTemplate(): string {
        if (this.getOption("section") === "chargebacks") {
            return chargebacks_template;
        }
        
        if (this.getOption("section") === "sales_refunds") {
            return sales_refunds_template;
        }
        
        if (this.getOption("section") === "authorizations") {
            return authorizations_template;
        }
        
        throw new Error("Unknown section name");
    },
    events: {
        "click .secondary-toggle label": "toggleSecondaryFields",
        "change input": "inputChanged",
        "keyup input": "inputChanged",
        "click .clear-icon": "clearInput",
        submit: "formSubmitted",
        reset: "clearForm"
    },
    ui: {
        secondaryToggle: ".secondary-toggle",
        secondaryFields: ".secondary-fields",
        first6: ".first-six input",
        last4: ".last-four input",
        ccnunTip: ".ccnum-tip"
    },
    
    initialize() {
        this.getFormDataFromURL();
    },
    
    clearForm(e) {
        this.formData = new SearchFormModel({}, {
            section: this.getOption("section")
        });
        this.onRender();
        this.triggerMethod("form:cleared", e);
    },
    
    clearInput(e) {
        const field = e.currentTarget.parentNode.querySelector("input");
        field.value = "";
        this.inputChanged({ currentTarget: field });
    },
    
    renderAmountFields() {
        const { amountFields } = this.getOption("formConfig");
        const data = this.formData.toJSON();
        
        amountFields.forEach((ap: AmountPickerFieldType) => {
            const apSelector = `.${ap.selector}`;
            const regionName = `region_${ap.selector}`;
            this.addRegion(regionName, apSelector);
            const dd = ap.dropdown;
            if (!data[ap.selector]) {
                data[ap.dropdown.selector] = store.getState().common.merchant_config.default_currency;
                this.formData.set(ap.dropdown.selector, data[ap.dropdown.selector]);
            }
            const dropDown = this.createDropDown(dd.apiKey, dd.selector, dd.useTypeAheadCollection, data);
            const value = data[ap.input.selector] || "";
            const label = ap.primary ? `<i class="fas fa-asterisk form-asterisk"></i>${ap.label}` : `${ap.label}`;
            const aPicker = new AmountPicker({
                pickerRoot: ap.pickerRoot, dropDown, value, label
            });
            this.showChildView(regionName, aPicker);
        });
    },
    
    renderDateFields() {
        const data = this.formData.toJSON();
        this.getOption("formConfig").dateFields.forEach(({ selector, minDate }) => {
            const regionName = `region_${selector}`;
            this.addRegion(regionName, `.${selector}`);
            const range = data[`${selector}_start`] ? [
                moment.utc(data[`${selector}_start`]),
                moment.utc(data[`${selector}_end`])
            ] : [
                moment.utc(),
                moment.utc()
            ];
            
            const datepickerInstance = new DatePicker({
                start: range[0],
                end: range[1],
                minDate,
                startFieldEmpty: !data[`${selector}_start`],
                showClear: true,
                normalizePickerValue: (picker) => [
                    picker.startDate.format("YYYY-MM-DD"),
                    picker.endDate.format("YYYY-MM-DD")
                ]
            });
            this.listenTo(datepickerInstance, "daterange:changed", (r: string) => {
                this.setDateRange(selector, r);
            });
            this.listenTo(datepickerInstance, "daterange:clear", (dpicker) => {
                dpicker.$el.find(".daterange").html("");
                this.setDateRange(selector, ["", ""]);
            });
            this.showChildView(regionName, datepickerInstance);
        });
    },
    
    setDateRange(s: string, r: Array<string>) {
        this.formData.set(s, `${r[0]}_${r[1]}`);
    },
    
    getSearchOptions(): Qualifiers {
        return this.getOption("qualifiers");
    },
    
    onRender() {
        this.renderDateFields();
        this.renderDropDowns();
        this.renderAmountFields();
    },
    
    onAttach() {
        this.populateStandardInputs();
        this.bindCleavesToCCNUm();
    },
    
    toggleSecondaryFields() {
        this.ui.secondaryToggle.toggleClass("on");
        this.ui.secondaryFields.toggleClass("on");
    },
    
    bindCleavesToCCNUm() {
        this.firstsix = new Cleave(this.ui.first6[0], {
            numeral: true,
            numeralDecimalScale: 0,
            numeralDecimalMark: "",
            delimiter: "",
            stripLeadingZeroes: false
        });
        this.lastfour = new Cleave(this.ui.last4[0], {
            numeral: true,
            numeralDecimalScale: 0,
            numeralDecimalMark: "",
            delimiter: "",
            stripLeadingZeroes: false
        });
    },
    
    populateStandardInputs() {
        const data = this.formData.toJSON();
        const { inputs } = this.getOption("formConfig");
        inputs.forEach((i: inputFieldType) => {
            this.populateInput(i, data);
        });
    },
    
    populateInput(i: inputFieldType, data) {
        const { selector } = i;
        const value = data[selector] || "";
        this.$(`input[name=${selector}]`).val(value);
        if (value) {
            this.$(`input[name=${selector}]`).parent().addClass("hasval");
        }
    },
    
    inputChanged(e) {
        const el = e.currentTarget;
        const { name, value } = el;
        
        if (name && (name === "iin" || name === "last_four")) {
            this.ccnumValidation(name);
        }
        if (name && name !== "amount_type") {
            if (value) {
                el.parentNode.classList.add("hasval");
            } else {
                el.parentNode.classList.remove("hasval");
            }
            this.formData.set(name, value);
        }
    },
    
    ccnumValidation(name) {
        const f6 = this.ui.first6.val();
        const l4 = this.ui.last4.val();
        const empty = !f6 && !l4;
        const firstOk = f6.length === 6 || f6.length === 8;
        const lastOk = l4.length === 2 || l4.length === 4;
        
        if (!empty && !(firstOk && lastOk)) {
            this.ui.ccnunTip[0].className = "ccnum-tip warning";
        } else {
            this.ui.ccnunTip[0].className = "ccnum-tip";
        }
        
        if (name === "iin") {
            if ((f6.length === 8) && (l4.length < 4)) {
                this.ui.last4.focus();
            }
        } else if ((l4.length === 4) && (f6.length < 8)) {
            this.ui.first6.focus();
        }
    },
    
    renderDropDowns() {
        const data = this.formData.toJSON();
        const { dropDownFields } = this.getOption("formConfig");
        dropDownFields.forEach((dd: DropDownFieldType) => {
            const regionName = `region_${dd.selector}`;
            
            const region = this.addRegion(regionName, `.${dd.selector}`);
            if (region.$el.length) {
                const dropDown = this.createDropDown(dd.apiKey, dd.selector, dd.useTypeAheadCollection, data);
                this.showChildView(regionName, dropDown);
            }
        });
    },
    
    createDropDown(dim: string, fieldName: string, useLabels: boolean, data) {
        let collection = [];
        
        if (useLabels) {
            // Use filterbar data
            const allLabels = this.getOption("filterBarData")[dim] || [];
            // Here we add empty option
            if (allLabels[0] && allLabels[0].alias !== "") {
                allLabels.unshift({ alias: "", ref: "", pivot: dim });
            }
            let first = true;
            allLabels.reduce((memo: Array<DropDownItemType>, item: DropDownItemType): Array<DropDownItemType> => {
                if (item.ref) {
                    if (item.ref === data[fieldName]) {
                        memo.push({
                            selected: true,
                            name: item.alias,
                            value: item.ref,
                            pivot: item.pivot
                        });
                    } else {
                        memo.push({
                            selected: false,
                            name: item.alias,
                            value: item.ref,
                            pivot: item.pivot
                        });
                    }
                } else if (first) {
                    // TODO this cleanup is to remove a null from the list.
                    // Should really be removed from the source typeahead function that makes the collection
                    memo.push({
                        selected: false,
                        name: item.alias,
                        value: item.ref,
                        pivot: item.pivot
                    });
                }
                first = false;
                return memo;
            }, collection);
        } else {
            // Use dimensions
            const dims = this.getOption("dimensions");
            // will become dropdown options
            const points = dims[dim].sort();
            // that adds empty option if not there
            if (points[0] && points[0] !== "") {
                points.unshift("");
            }

            // from list of options generate a collection for dropdown
            const pointsObjs = points.map((p: string) => {
                const item: DropDownItemType = { name: p, value: p };
                if (p === data[fieldName]) {
                    return { selected: true, ...item };
                }
                return item;
            });
            collection = new Backbone.Collection(pointsObjs);
        }
        
        return new Dropdown({
            collection,
            showFilterInput: true,
            showClearIcon: dim !== "currency_alias",
            direction: {
                horizontal: "left"
            },
            onChangeValue: (newValue: string) => {
                this.formData.set(fieldName, newValue);
            }
        });
    },
    
    getFormDataFromURL() {
        // Pick only first item in the array
        const props = this.getSearchOptions().value.reduce((memo, curr) => ({
            ...memo,
            [curr.category]: curr.values.map((v) => v.value)[0]
        }), {});
        
        this.formData = new SearchFormModel(props, {
            section: this.getOption("section")
        });
    },
    
    formSubmitted(e) {
        e.preventDefault();
        if (this.formData.isValid()) {
            const data = this.formData.toJSON();
            this.triggerMethod("form:submitted", e, data);
        } else {
            const errors = [...this.formData.validationError];
            sweetalert({
                icon: "warning",
                className: "search-alert",
                title: "Let's try this again",
                text: errors.join("\n\n"),
                buttons: {
                    cancel: {
                        text: "OK",
                        visible: true
                    }
                }
            });
        }
    },
    
    onDestroy() {
        if (this.firstsix) {
            this.firstsix.destroy();
        }
        
        if (this.lastfour) {
            this.lastfour.destroy();
        }
    }
});
